import { formatDate } from "@helmr/ui";
import type { BadgeTone } from "@helmr/ui";
import type { MemberRow, PlanSummary } from "../../api/members";
import type { Plan } from "../../api/plans";
import { addDays } from "../../lib/dates";
import { t } from "./strings";

/**
 * Days ahead that count as "expiring". The follow-ups rule (`renewalLeadDays`, default 15) is a setting with no route
 * that exposes it yet, so the app uses the default. Open question in the PR. It only picks which chip to show: whether a
 * plan is active, frozen or expired always comes from the API (computed on read, branch-local).
 */
export const EXPIRING_LEAD_DAYS = 15;

export interface ChipSpec { key: "active" | "expiring" | "expired" | "frozen" | "upcoming" | "none" | "dues"; label: string; tone: BadgeTone }

const SPEC: Record<ChipSpec["key"], Pick<ChipSpec, "label" | "tone">> = {
  active: { label: t.status.active, tone: "ok" },
  expiring: { label: t.status.expiring, tone: "warn" },
  expired: { label: t.status.expired, tone: "crit" },
  frozen: { label: t.status.frozen, tone: "info" },
  upcoming: { label: t.status.upcoming, tone: "neutral" },
  none: { label: t.status.none, tone: "neutral" },
  dues: { label: t.status.dues, tone: "crit" },
};
const chip = (key: ChipSpec["key"]): ChipSpec => ({ key, ...SPEC[key] });

/** A list row carries only its current plan, so "expiring" cannot see a renewal queued behind it (the filter can). */
export function rowChips(plan: PlanSummary | null, today: string): ChipSpec[] {
  if (!plan) return [chip("none")];
  if ((plan.status === "active" || plan.status === "frozen") && plan.endDate <= addDays(today, EXPIRING_LEAD_DAYS)) return [chip(plan.status), chip("expiring")];
  return [chip(plan.status)];
}

const isOpen = (p: Plan) => p.status === "active" || p.status === "frozen" || p.status === "upcoming";

/** Profile chips from the plans the API computed, plus Dues when the balance is visible and owed. */
export function profileChips(plans: readonly Plan[], balanceDue: number | null, today: string): ChipSpec[] {
  const memberships = plans.filter((p) => p.kind === "membership");
  const running = memberships.filter((p) => p.status === "active" || p.status === "frozen");
  const out: ChipSpec[] = [];
  if (running.length > 0) {
    const current = running.reduce((a, b) => (b.endDate > a.endDate ? b : a));
    const queuedBehind = memberships.some((p) => isOpen(p) && p.endDate > current.endDate);
    out.push(chip(current.status as "active" | "frozen"));
    if (!queuedBehind && current.endDate <= addDays(today, EXPIRING_LEAD_DAYS)) out.push(chip("expiring"));
  } else if (memberships.some((p) => p.status === "upcoming")) out.push(chip("upcoming"));
  else if (memberships.length > 0) out.push(chip("expired"));
  else out.push(chip("none"));
  if (balanceDue !== null && balanceDue > 0) out.push(chip("dues"));
  return out;
}

export interface TrainAnswer { tone: "ok" | "crit" | "warn"; headline: string; reason: string }

/** The first answer on the profile. Reads the API's computed plan statuses; it decides nothing about validity itself. */
export function goodToTrain(plans: readonly Plan[], today: string, locale: string): TrainAnswer {
  const memberships = plans.filter((p) => p.kind === "membership");
  const active = memberships.filter((p) => p.status === "active");
  if (active.length > 0) {
    const end = active.reduce((a, b) => (b.endDate > a.endDate ? b : a)).endDate;
    return { tone: "ok", headline: "Active", reason: `Plan runs to ${formatDate(end, locale, "short")}` };
  }
  const frozen = memberships.find((p) => p.status === "frozen");
  if (frozen) {
    const f = frozen.freezes.find((x) => x.endedAt === null && x.from <= today && today <= (x.actualTo ?? x.to));
    return { tone: "crit", headline: "Frozen", reason: f ? `Plan is frozen until ${formatDate(f.actualTo ?? f.to, locale, "short")}` : "Plan is frozen" };
  }
  const upcoming = memberships.filter((p) => p.status === "upcoming").sort((a, b) => a.startDate.localeCompare(b.startDate))[0];
  if (upcoming) return { tone: "crit", headline: "Not started", reason: `Plan starts ${formatDate(upcoming.startDate, locale, "short")}` };
  if (memberships.length > 0) {
    const last = memberships.reduce((a, b) => (b.endDate > a.endDate ? b : a));
    return { tone: "crit", headline: "Expired", reason: `Last plan ${last.status === "expired" ? "ended" : "was closed"} ${formatDate(last.endDate, locale, "short")}` };
  }
  return { tone: "crit", headline: "No plan", reason: "No membership plan on file" };
}

export const KIND_LABEL: Record<Plan["kind"], string> = { membership: "Membership", pt_pack: "PT pack", class_pack: "Class pack" };
export const STATUS_TONE: Record<string, BadgeTone> = { active: "ok", frozen: "info", upcoming: "neutral", expired: "crit", exhausted: "neutral", cancelled: "neutral", transferred: "neutral", upgraded: "neutral" };
